<script setup>
import { ref, onMounted, computed, nextTick, watch } from "vue";
import api from "@/api/axios";
import MiniStatisticsCard from "@/modules/shared/components/Cards/MiniStatisticsCard.vue";
import * as yup from "yup";
import { useForm, Field, ErrorMessage, useFieldArray } from "vee-validate";
import Swal from "sweetalert2";

const primerInput = ref(null);
function setPrimerInput(el, index) {
  if (index === 0 && el) {
    // Almacena la referencia completa del componente <Field>
    primerInput.value = el;
  }
}

const allCuentas = ref([]);
const allClientes = ref([]);

// Estado para el modal de confirmación
const showConfirmModal = ref(false);
const totalVentaAConfirmar = ref(0);
const cuentaAConfirmar = ref("");
const payloadAConfirmar = ref(null);

// Estado y cálculos para "Cerrar caja"
const showCerrarCajaModal = ref(false);
const porcentajeUtilidad = ref(0);
const cuentaOrigenSeleccionada = ref(""); // Nueva variable para la cuenta origen
const totalDelDia = computed(() => Number(estadisticas.value?.ventasTotales ?? 0));
const utilidadMonto = computed(() => Math.round(totalDelDia.value * (porcentajeUtilidad.value / 100)));
const reinversionMonto = computed(() => Math.max(0, totalDelDia.value - utilidadMonto.value));

const abrirCerrarCaja = () => {
  // Validar que el total del día sea mayor a 0
  if (totalDelDia.value <= 0) {
    window.llamarToast && window.llamarToast("No se puede cerrar caja sin ventas del día", "error");
    return;
  }

  porcentajeUtilidad.value = 0;
  // Inicializar con la primera cuenta disponible
  cuentaOrigenSeleccionada.value = allCuentas.value[0]?.cuen_id ?? "";
  showCerrarCajaModal.value = true;
};

const enviarCerrarCaja = async () => {
  if (porcentajeUtilidad.value < 0 || porcentajeUtilidad.value > 100) {
    window.llamarToast && window.llamarToast("El porcentaje debe estar entre 0 y 100", "error");
    return;
  }

  if (!cuentaOrigenSeleccionada.value) {
    window.llamarToast && window.llamarToast("Debe seleccionar una cuenta origen", "error");
    return;
  }

  // Buscar el nombre de la cuenta para mostrar en la confirmación
  const cuentaOrigen = allCuentas.value.find(c => c.cuen_id === cuentaOrigenSeleccionada.value);
  const nombreCuentaOrigen = cuentaOrigen ? cuentaOrigen.cuen_nombre : "N/A";

  const result = await Swal.fire({
    title: "Confirmar cierre de caja",
    html: `<div style="text-align:left">
      <strong>Total Ferretería:</strong> ${estadisticas.value.ventasFerreteria?.total.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })}<br/>
      <strong>Total Hierro:</strong> ${estadisticas.value.ventasHierro?.total.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })}<br/>
      <strong>Total del día:</strong> ${totalDelDia.value.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })}<br/>
      <strong>Cuenta origen:</strong> ${nombreCuentaOrigen}<br/>
      <strong>Utilidad (${porcentajeUtilidad.value}%):</strong> ${utilidadMonto.value.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })}<br/>
      <strong>Reinversión:</strong> ${reinversionMonto.value.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })}
      </div>`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Sí, cerrar caja",
    cancelButtonText: "Cancelar",
  });

  if (result.isConfirmed) {
    try {
      const response = await api.post("dashboard/cerrar-caja", {
        porcentaje_utilidad: porcentajeUtilidad.value,
        total_ventas: totalDelDia.value,
        utilidad: utilidadMonto.value,
        reinversion: reinversionMonto.value,
        cuenta_origen_id: cuentaOrigenSeleccionada.value,
      });

      if (!response.data.data.success) {
        throw new Error(response.data.data.mensaje);
      } else {
        await Swal.fire("Caja cerrada", "El cierre de caja se registró correctamente", "success");
        showCerrarCajaModal.value = false;
        await fetchResumen();
      }

    } catch (error) {
      console.error("Error al cerrar caja:", error);
      await Swal.fire("Error", error.message, "error");
    }
  }
};

const { handleSubmit, resetForm, values, setFieldValue } = useForm({
  validationSchema: yup.object({
    vepa_fecha_aplicacion: yup
      .date()
      .required("La fecha de aplicación es obligatoria")
      .min(
        new Date().toISOString().split("T")[0],
        "La fecha de aplicación debe ser posterior o igual a hoy"
      )
      .typeError("Debe ser una fecha válida"),
    vent_estado_pago: yup.string().required("El estado de pago es obligatorio"),
    cuen_id: yup.string().required("Debe seleccionar una cuenta"),
    clie_id: yup.string().required("Debe seleccionar un cliente"),
    vent_descripcion: yup
      .string()
      .nullable()
      .max(200, "La descripción no puede exceder los 200 caracteres"),
    productos: yup
      .array()
      .of(
        yup.object({
          vede_producto: yup.string().required("El producto es obligatorio"),
          vede_cantidad: yup
            .number()
            .required("La cantidad es obligatoria")
            .min(0.1, "La cantidad debe ser al menos 0.1")
            .typeError("Debe ser un número"),
          vede_total: yup
            .number()
            .required("El total es obligatorio")
            // .min(100, "El total debe ser al menos 100")
            .typeError("Debe ser un número"),
        })
      )
      .required()
      .min(1, "Debe haber al menos un producto"),
  }),
  initialValues: {
    vepa_fecha_aplicacion: new Date().toISOString().split("T")[0],
    vent_estado_pago: "5",
    vent_descripcion: "",
    productos: [
      { vede_producto: "Ferre10", vede_cantidad: 1, vede_total: 0 },
    ],
  },
});

const { push, remove, fields } = useFieldArray("productos");

onMounted(async () => {
  const resC = await api.get("/cuentas?search=FISICA&columna_filtro=cuen_tipo");
  allCuentas.value = resC.data.data.data;
  setFieldValue("cuen_id", allCuentas.value[0]?.cuen_id ?? "");

  const resCl = await api.get("/clientes");
  allClientes.value = resCl.data.data.data;
  setFieldValue("clie_id", allClientes.value[0]?.clie_id ?? "");

  await nextTick();
  // Accede al elemento del DOM real dentro del componente <Field>
  primerInput.value?.el?.focus();
  primerInput.value?.el?.select();

  await fetchResumen();
});

const props = defineProps({
  item: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["submitted"]);

// Función para enviar el formulario real, solo se llama después de la confirmación
const submitFinalForm = async () => {
  // Cerrar el modal
  showConfirmModal.value = false;

  // Aquí usamos el payload que ya fue filtrado y preparado
  const finalPayload = payloadAConfirmar.value;

  try {
    await api.post("/ventas", finalPayload);
    emit("submitted", finalPayload);
    resetForm();
    setFieldValue("clie_id", allClientes.value[0]?.clie_id ?? "");
    setFieldValue("cuen_id", allCuentas.value[0]?.cuen_id ?? "");

    await fetchResumen();
  } catch (error) {
    console.error("Error al registrar la venta:", error);
  }
};

// Nueva función para manejar el pre-envío con confirmación
const handlePreSubmit = handleSubmit(async (values) => {
  // Filtrar los productos para remover el último si está vacío
  const productosFiltrados = values.productos.filter((p, index) => {
    // Mantiene todos los productos excepto el último si su total es 0 y no es el único producto
    return (
      fields.value.length === 1 ||
      index < fields.value.length - 1 ||
      p.vede_total > 0
    );
  });

  // Validar si hay productos válidos para enviar
  if (productosFiltrados.length === 0) {
    // En caso de que se intente enviar sin productos válidos
    // Podrías mostrar un mensaje de error aquí.
    // En este caso, el `min(1)` en la validación de `yup` ya lo maneja
    return;
  }

  const payload = {
    ...values,
    productos: productosFiltrados,
    vent_total: productosFiltrados.reduce(
      (acc, p) => acc + Number(p.vede_total || 0),
      0
    ),
    vent_iva: 0,
    vent_fecha: new Date(values.vepa_fecha_aplicacion)
      .toISOString()
      .split("T")[0],
  };

  // Almacenar el payload para usarlo en el envío final
  payloadAConfirmar.value = payload;

  // Buscar el nombre de la cuenta para mostrar en el modal
  const cuentaSeleccionada = allCuentas.value.find(
    (c) => c.cuen_id === values.cuen_id
  );
  cuentaAConfirmar.value = cuentaSeleccionada
    ? cuentaSeleccionada.cuen_nombre
    : "N/A";

  // Establecer el total de la venta para mostrar en el modal
  totalVentaAConfirmar.value = payload.vent_total;

  // Mostrar el modal de confirmación
  showConfirmModal.value = true;
});

watch(
  () => props.item,
  (it) => {
    if (it) {
      resetForm({
        values: {
          vepa_fecha_aplicacion: it.vepa_fecha_aplicacion,
          vent_estado_pago: it.vent_estado_pago,
          vent_descripcion: it.vent_descripcion,
        },
      });
      fields.value = it.productos.map((p) => ({ ...p }));
    }
  },
  { immediate: true }
);

const estadisticas = ref({});

const totalVenta = computed(() =>
  fields.value.reduce((acc, p) => acc + Number(p.value.vede_total || 0), 0)
);

const scrollBottom = ref(null);

// Función modificada para manejar la creación de nuevas filas y el desplazamiento
const verificarUltimo = async (index, event) => {
  // const isInputEvent = event && event.type === 'input';
  const isTabEvent = event && event.key === "Tab";

  // Si es un evento de teclado, detenemos la propagación para evitar
  // que el tab se mueva al siguiente elemento del DOM
  if (isTabEvent) {
    event.preventDefault();
  }

  const ultimo = fields.value.length - 1;
  const producto = values.productos?.[ultimo];
  if (
    index === ultimo &&
    producto?.vede_producto.trim() !== "" &&
    (producto?.vede_total || 0) > 0
  ) {
    push({ vede_producto: "Ferre10", vede_cantidad: 1, vede_total: 0 });
    await nextTick();
    if (scrollBottom.value) {
      scrollBottom.value.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }

  }
};

const isLoading = ref(true);

const fetchResumen = async () => {
  try {
    const { data } = await api.get("/dashboard");
    estadisticas.value = data.data;
    const cuentaNequi = data.data.ventasPorCuenta.find(
      (item) => item.cuenta?.cuen_nombre === "NEQUI PRINCIPAL"
    );
    estadisticas.value.nequi = {
      nombre: cuentaNequi?.cuenta?.cuen_nombre ?? "NEQUI PRINCIPAL",
      numero: cuentaNequi?.cuenta?.cuen_numero ?? "",
      total: parseFloat(cuentaNequi?.total_ventas ?? 0),
    };
    const cuentaBancolombia = data.data.ventasPorCuenta.find(
      (item) => item.cuenta?.cuen_nombre === "BANCOLOMBIA"
    );
    estadisticas.value.bancolombia = {
      nombre: cuentaBancolombia?.cuenta?.cuen_nombre ?? "BANCOLOMBIA",
      numero: cuentaBancolombia?.cuenta?.cuen_numero ?? "",
      total: parseFloat(cuentaBancolombia?.total_ventas ?? 0),
    };
    const cajaEfectivo = data.data.ventasPorCuenta.find(
      (item) => item.cuenta?.cuen_nombre === "CAJA"
    );
    estadisticas.value.efectivo = {
      nombre: cajaEfectivo?.cuenta?.cuen_nombre ?? "Efectivo",
      numero: cajaEfectivo?.cuenta?.cuen_numero ?? "",
      total: parseFloat(cajaEfectivo?.total_ventas ?? 0),
    };
    const ventasFiadas = data.data.ventasFiadas;
    estadisticas.value.ventasFiadas = {
      nombre: ventasFiadas?.cuenta?.cuen_nombre ?? "Ventas Fiadas",
      numero: ventasFiadas?.cuenta?.cuen_numero ?? "",
      total: parseFloat(ventasFiadas ?? 0),
    };
    const ventasFerreteria = data.data.ventasProductos.find(
      (item) => item.vede_producto === "Ferre10"
    );
    estadisticas.value.ventasFerreteria = {
      nombre: "Ferre10",
      total: parseFloat(ventasFerreteria?.total_vendido ?? 0),
    };
    const ventasHierro = data.data.ventasProductos.find(
      (item) => item.vede_producto === "Hierro"
    );
    estadisticas.value.ventasHierro = {
      nombre: "Hierro",
      total: parseFloat(ventasHierro?.total_vendido ?? 0),
    };
    const totalMes = data.data.ventasTotalesMes;
    estadisticas.value.totalMes = {
      nombre: "Total Mes",
      numero: "",
      total: parseFloat(totalMes ?? 0),
    };

  } catch (error) {
    console.error("Error al cargar resumen:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchResumen);

const formatCurrency = (value) => {
  if (!value || isNaN(value)) return '$0'
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP'
  }).format(value)
}
</script>
<template>
  <div class="py-4 container-fluid">
    <!-- Modal de Confirmación -->
    <div v-if="showConfirmModal" class="modal-overlay">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar Venta</h5>
            <button type="button" class="btn-close" @click="showConfirmModal = false"></button>
          </div>
          <div class="modal-body">
            <p>¿Estás seguro de que deseas registrar esta venta?</p>
            <p>
              <strong>Total de la Venta:</strong>
              {{
                totalVentaAConfirmar.toLocaleString("es-CO", {
                  style: "currency",
                  currency: "COP",
                  minimumFractionDigits: 0,
                })
              }}
            </p>
            <p><strong>Cuenta:</strong> {{ cuentaAConfirmar }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showConfirmModal = false">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="submitFinalForm">
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Fin del Modal -->
    <!-- Modal Cerrar Caja -->
    <div v-if="showCerrarCajaModal" class="modal-overlay">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Cerrar caja</h5>
            <button type="button" class="btn-close" @click="showCerrarCajaModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-2">
              <strong>Total Ferre10:</strong>
              {{ (estadisticas.ventasFerreteria?.total || 0).toLocaleString('es-CO', {
                style: 'currency', currency: 'COP', minimumFractionDigits:
                  0
              }) }}
            </div>
            <div class="mb-2">
              <strong>Total Hierro:</strong>
              {{ (estadisticas.ventasHierro?.total || 0).toLocaleString('es-CO', {
                style: 'currency', currency: 'COP', minimumFractionDigits:
                  0
              }) }}
            </div>
            <div class="mb-2">
              <strong>Total del día:</strong>
              {{ (totalDelDia || 0).toLocaleString('es-CO', {
                style: 'currency', currency: 'COP', minimumFractionDigits:
                  0
              }) }}
            </div>
            <!-- Selector de cuenta origen -->
            <div class="mb-3">
              <label class="form-label">Cuenta origen <span class="text-danger">*</span></label>
              <select class="form-select" v-model="cuentaOrigenSeleccionada">
                <option value="" disabled>Seleccione la cuenta de donde se tomará el dinero</option>
                <option v-for="cuenta in allCuentas" :key="cuenta.cuen_id" :value="cuenta.cuen_id">
                  {{ cuenta.cuen_nombre }} - Saldo: {{ formatCurrency(cuenta.cuen_saldo) }}
                </option>
              </select>
              <small class="text-muted">La cuenta de la cual se tomará el dinero para las utilidades.</small>
            </div>
            <div class="mb-3">
              <label class="form-label">Porcentaje a utilidades (%)</label>
              <input type="number" class="form-control" v-model.number="porcentajeUtilidad" min="0" max="100" />
              <small class="text-muted">El restante irá a reinversión.</small>
            </div>
            <div class="mb-2">
              <div><strong>Utilidad:</strong> {{ utilidadMonto.toLocaleString('es-CO', {
                style: 'currency', currency:
                  'COP', minimumFractionDigits: 0
              }) }}</div>
              <div><strong>Reinversión:</strong> {{ reinversionMonto.toLocaleString('es-CO', {
                style: 'currency',
                currency: 'COP', minimumFractionDigits: 0
              }) }}</div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showCerrarCajaModal = false">Cancelar</button>
            <button type="button" class="btn btn-primary" @click="enviarCerrarCaja">Aceptar</button>
          </div>
        </div>
      </div>
    </div>
    <!-- Fin Modal Cerrar Caja -->
    <div class="row">
      <div class="col-lg-12">
        <div class="row">
          <div class="col-lg-4 col-md-6 col-12">
            <mini-statistics-card title="Dinero de Hoy" :value="(estadisticas.ventasTotales ?? 0).toLocaleString('es-CO', {
              style: 'currency',
              currency: 'COP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
              " :icon="{
                component: 'fa fa-money-bills',
                background: 'bg-gradient-success',
                shape: 'rounded-circle',
              }" />
          </div>
          <div class="col-lg-4 col-md-6 col-12">
            <mini-statistics-card :title="estadisticas.efectivo?.nombre ?? 'Caja'" :value="(estadisticas.efectivo?.total ?? 0).toLocaleString('es-CO', {
              style: 'currency',
              currency: 'COP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
              " :icon="{
                component: 'fa fa-cash-register',
                background: 'bg-gradient-primary',
                shape: 'rounded-circle',
              }" />
          </div>
          <div class="col-lg-4 col-md-6 col-12">
            <div>
              <mini-statistics-card class="valor-oculto" :title="estadisticas.totalMes?.nombre ?? 'Total Mes'" :value="(estadisticas.totalMes?.total ?? 0).toLocaleString('es-CO', {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })" :icon="{
                component: 'fa fa-calendar-days',
                background: 'bg-gradient-danger',
                shape: 'rounded-circle',
              }" />
            </div>
          </div>

          <div class="col-lg-4 col-md-6 col-12">
            <mini-statistics-card :title="estadisticas.bancolombia?.nombre ?? 'Bancolombia'" :value="(estadisticas.bancolombia?.total ?? 0).toLocaleString('es-CO', {
              style: 'currency',
              currency: 'COP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
              " :icon="{
                component: 'fa fa-qrcode',
                // component: 'ni ni-paper-diploma',
                background: 'bg-gradient-dark',
                shape: 'rounded-circle',
              }" />
          </div>
          <div class="col-lg-4 col-md-6 col-12">
            <mini-statistics-card :title="estadisticas.ventasFiadas?.nombre ?? 'Ventas Fiadas'" :value="(estadisticas.ventasFiadas?.total ?? 0).toLocaleString(
              'es-CO',
              {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }
            )
              " :icon="{
                component: 'fa fa-piggy-bank',
                // component: 'ni ni-cart',
                background: 'bg-gradient-warning',
                shape: 'rounded-circle',
              }" />
          </div>
          <div class="col-lg-4 col-md-6 col-12">
            <div class="mb-3 card" :class="{ 'card-disabled': totalDelDia <= 0 }">
              <div class="p-3 card-body" @click="totalDelDia > 0 ? abrirCerrarCaja() : null"
                :style="{ cursor: totalDelDia > 0 ? 'pointer' : 'not-allowed' }"
                :title="totalDelDia <= 0 ? 'No se puede cerrar caja sin ventas del día' : 'Cerrar caja'">
                <div class="d-flex flex-row-reverse justify-content-between">
                  <div class="text-center shadow icon icon-shape bg-gradient-info rounded-circle">
                    <i class="fa fa-lock text-lg opacity-10" aria-hidden="true"></i>
                  </div>
                  <div>
                    <div class="numbers">
                      <p class="mb-0 text-sm text-uppercase font-weight-bold"
                        :class="totalDelDia <= 0 ? 'text-muted' : 'text-dark'">
                        Cerrar caja
                      </p>
                      <!-- <h5 class="mb-0 font-weight-bolder" :class="totalDelDia <= 0 ? 'text-muted' : 'text-dark'">
                        {{ totalDelDia <= 0 ? 'Sin ventas' : 'Disponible' }}
                      </h5> -->
                      <p class="mt-0 mb-0 text-muted" style="font-size: 0.875rem;">
                        {{ totalDelDia <= 0 ? 'Realiza ventas para cerrar caja' : 'Haz clic para cerrar la caja del día'
                        }} </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-2 col-md-6 col-12">
            <div class="mb-3">
              <label class="form-label">Fecha De Transacción</label>
              <Field name="vepa_fecha_aplicacion" type="date" class="form-control" />
              <ErrorMessage name="vepa_fecha_aplicacion" class="text-danger small" />
            </div>
          </div>
          <div class="col-lg-2 col-md-6 col-12">
            <label class="form-label">Estado De Pago</label>
            <Field name="vent_estado_pago" as="select" class="form-select">
              <option selected value="5">Pagada</option>
              <option value="8">Pendiente</option>
            </Field>
            <ErrorMessage name="vent_estado_pago" class="text-danger small" />
          </div>
          <div class="col-lg-3 col-md-6 col-12">
            <label class="form-label">Cuenta</label>
            <Field name="cuen_id" as="select" class="form-select">
              <option disabled value="">Seleccione la Cuenta de Pago</option>
              <option v-for="cuenta in allCuentas" :key="cuenta.cuen_id" :value="cuenta.cuen_id">
                {{ cuenta.cuen_nombre }}
              </option>
            </Field>
            <ErrorMessage name="cuen_id" class="text-danger" />
          </div>
          <div class="col-lg-5 col-md-6 col-12">
            <label class="form-label">Cliente</label>
            <Field name="clie_id" as="select" class="form-select">
              <option disabled value="">Seleccione el Cliente</option>
              <option v-for="cliente in allClientes" :key="cliente.clie_id" :value="cliente.clie_id">
                {{ cliente.clie_documento }} - {{ cliente.clie_nombre }}
                {{ cliente.clie_nombre }}
              </option>
            </Field>
            <ErrorMessage name="clie_id" class="text-danger" />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-8">
            <div class="row d-flex align-items-center">
              <div class="col-lg-12">
                <div v-for="(field, index) in fields" :key="field.key" class="mb-3 border border-primary rounded row">
                  <div class="my-0 px-1 col-lg-4 col-md-6 col-12">
                    <label class="mb-1">Destino</label>
                    <Field :name="`productos[${index}].vede_producto`" as="select" class="form-select my-0 py-0 px-1"
                      :ref="(el) => setPrimerInput(el, index)">
                      <option selected value="Ferre10">Ferre10</option>
                      <option value="Hierro">Hierro</option>
                    </Field>
                    <ErrorMessage :name="`productos[${index}].vede_producto`" class="text-danger small" />
                  </div>
                  <div class="my-0 px-1 col-lg-4 col-md-6 col-12">
                    <label class="mb-1">Cantidad</label>
                    <Field :name="`productos[${index}].vede_cantidad`" type="number" class="form-control my-0 py-0 px-1"
                      placeholder="Ej: 1" step="0.1" min="0.1" max="100000000" />
                    <ErrorMessage :name="`productos[${index}].vede_cantidad`" class="text-danger small" />
                  </div>
                  <div class="my-0 px-1 col-lg-4 col-md-6 col-12 d-flex flex-column align-items-end">
                    <div class="w-100">
                      <label class="mb-1">Total</label>
                      <div class="m-0 input-group d-flex align-items-center">
                        <Field :name="`productos[${index}].vede_total`" type="number"
                          class="form-control my-0 py-0 px-1" @input="verificarUltimo(index, $event)"
                          @keyup.tab="verificarUltimo(index, $event)" min="0" />
                        <a href="#" @click="remove(index)" class="text-danger m-0 p-0">
                          <i class="fa fa-trash"></i>
                        </a>
                      </div>

                      <ErrorMessage :name="`productos[${index}].vede_total`" class="text-danger small" />
                    </div>
                    <!-- <button
                      type="button"
                      @click="remove(index)"
                      class="btn btn-danger btn-sm mt-1"
                      v-if="fields.length > 1"
                    >
                      Eliminar
                    </button> -->
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label>Descripción (opcional)</label>
                  <Field name="vent_descripcion" type="text" class="form-control" placeholder="Ej: Venta en efectivo" />
                  <ErrorMessage name="vent_descripcion" class="text-danger small" />
                </div>
              </div>
              <div class="col-12">
                <div ref="scrollBottom"></div>
                <button type="button" @click="handlePreSubmit" class="btn btn-primary w-100">
                  Registrar Venta
                </button>
              </div>
            </div>
          </div>
          <div class="col-lg-4 d-flex align-items-center justify-content-center">
            <div class="total-summary-card">
              <div class="text-center">
                <strong>Total Venta:</strong>
                <div class="fs-2 text-success">
                  {{
                    totalVenta.toLocaleString("es-CO", {
                      style: "currency",
                      currency: "COP",
                      minimumFractionDigits: 0,
                    })
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
/* Estilos para el modal de confirmación */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.total-summary-card {
  padding: 20px;
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  height: fit-content;
}

.card-disabled {
  opacity: 0.6;
  filter: grayscale(30%);
}

.card-disabled .card-body:hover {
  background-color: #f8f9fa !important;
}

.card:not(.card-disabled) .card-body:hover {
  background-color: rgba(0, 0, 0, 0.02);
  transform: translateY(-2px);
  transition: all 0.2s ease;
}

@media (min-width: 992px) {
  .row {
    align-items: stretch;
    /* Esto hace que las columnas tengan la misma altura */
  }
}

.valor-oculto {
  filter: blur(6px);
  transition: filter 0.3s ease;
}

.valor-oculto:hover {
  filter: blur(0);
}
</style>
